import type { ReactNode } from "react";
import { Row, Col, Pagination, Card } from "antd";
import type { PaginationProps } from "antd";
import { useTranslation } from "react-i18next";
import type { GridColumnMetadata, LifecycleInfo } from "../../types";
import { EmptyState } from "../EmptyState";
import { ItemCard } from "./ItemCard";

type RenderFn = (value: unknown, record: Record<string, unknown>) => ReactNode;

interface CardViewProps {
  dataSource: Record<string, unknown>[];
  columns: GridColumnMetadata[];
  resource: string;
  loading?: boolean;
  enableRowClick?: boolean;
  includeActions?: boolean;
  mergedRenderers: Record<string, RenderFn>;
  getRenderer: (col: GridColumnMetadata) => RenderFn;
  lifecycleMap: LifecycleInfo | null;
  pagination?: PaginationProps | false;
  isMobile?: boolean;
}

export function CardView({
  dataSource,
  columns,
  resource,
  loading,
  enableRowClick,
  includeActions,
  mergedRenderers,
  getRenderer,
  lifecycleMap,
  pagination,
  isMobile,
}: CardViewProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Row gutter={[16, 16]}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Col key={i} xs={24} sm={12} md={12} lg={8} xl={8} xxl={6}>
            <Card loading size="small" style={{ height: 180 }} />
          </Col>
        ))}
      </Row>
    );
  }

  if (!dataSource || dataSource.length === 0) {
    return <EmptyState resource={resource} />;
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        {dataSource.map((record) => {
          const id = record.id as string;
          return (
            <Col key={id} xs={24} sm={12} md={12} lg={8} xl={8} xxl={6}>
              <ItemCard
                record={record}
                columns={columns}
                resource={resource}
                enableRowClick={enableRowClick}
                includeActions={includeActions}
                mergedRenderers={mergedRenderers}
                getRenderer={getRenderer}
                lifecycleMap={lifecycleMap}
              />
            </Col>
          );
        })}
      </Row>

      {pagination && (
        <div style={{ marginTop: 16, display: "flex", justifyContent: "end" }}>
          <Pagination
            {...pagination}
            size={isMobile ? "small" : "default"}
            showSizeChanger={!isMobile}
            showQuickJumper={!isMobile}
            simple={isMobile}
            showTotal={(total) =>
              t("pagination.total", { total })
            }
          />
        </div>
      )}
    </div>
  );
}
